import type { WysiwygLinkType } from '../wysiwyg-extensions/link/wysiwyg-link.types';
import type { Placeholder } from '../wysiwyg-extensions/placeholders/placeholder';

export type Redactor = any;
// eslint-disable-next-line no-var
export declare var $R: Redactor;

export type RedactorApp = any;
export type RedactorComponentType = any;

export type RedactorUploadFn = (
  data: FormData,
  files: FileList,
  event: DragEvent,
  redactorInstance: any,
) => any;

export enum RedactorConfigContext {
  Simple = 'simple',
  Default = 'default',
  Rich = 'rich',
}

export interface RedactorSettings {
  // https://imperavi.com/redactor/docs/settings/autoparse/
  autoparse: boolean;
  autoparseStart: boolean;
  autoparsePaste: boolean;
  autoparseLinks: boolean;
  autoparseImages: boolean;
  autoparseVideo: boolean;

  // https://imperavi.com/redactor/docs/settings/autosave/
  autosave: boolean | string;
  autosaveName: boolean | string;
  autosaveData: boolean | object;

  // https://imperavi.com/redactor/docs/settings/button/
  buttons: string[];
  buttonsTextLabeled: boolean;
  buttonsAdd: string[];
  buttonsAddFirst: string[];
  buttonsAddAfter: boolean | { after: string; buttons: string[] };
  buttonsAddBefore: boolean | { before: string; buttons: string[] };
  buttonsHide: string[];
  buttonsHideOnMobile: string[];
  activeButtons: { [key: string]: string };
  activeButtonsAdd: { [key: string]: string };

  // https://imperavi.com/redactor/docs/settings/clean/
  cleanOnEnter: boolean;
  cleanInlineOnEnter: boolean;
  removeScript: boolean;
  removeNewLines: boolean;
  removeComments: boolean;
  replaceTags: { [key: string]: string };

  // https://imperavi.com/redactor/docs/settings/click-to-edit/
  clickToEdit: boolean;
  clickToSave: boolean;
  clickToCancel: boolean;

  // https://imperavi.com/redactor/docs/settings/file/
  fileUpload: boolean | string | RedactorUploadFn;
  fileUploadParam: string;
  fileData: object | boolean;
  fileAttachment: boolean | string;

  // https://imperavi.com/redactor/docs/settings/focus/
  focus: boolean;
  focusEnd: boolean;

  // https://imperavi.com/redactor/docs/settings/formatting/
  breakline: boolean;
  markup: string;
  enterKey: boolean;
  formatting: string[];
  formattingAdd: boolean | object;
  formattingHide: boolean | string[];

  // https://imperavi.com/redactor/docs/settings/height-and-width/
  minHeight: boolean | string;
  maxHeight: boolean | string;
  maxWidth: boolean | string;

  // https://imperavi.com/redactor/docs/settings/image/
  imageUpload: boolean | string | RedactorUploadFn;
  imageUploadParam: string;
  imageData: object | boolean;
  imageFigure: boolean;
  imageEditable: boolean;
  imageLink: boolean;
  imageCaption: boolean;
  imagePosition: boolean | object;
  imageFloatMargin: string;
  imageResizable: boolean;

  // https://imperavi.com/redactor/docs/settings/link/
  linkNewTab: boolean;
  linkTarget: boolean | string;
  linkTitle: boolean;
  linkNofollow: boolean;
  linkSize: number;
  linkValidation: boolean;

  // https://imperavi.com/redactor/docs/settings/misc/
  animation: boolean;
  lang: string;
  direction: string;
  spellcheck: boolean;
  structure: boolean;
  scrollTarget: boolean | string;
  placeholder: boolean | string;
  inline: boolean;
  plugins: string[];
  callbacks: object;
  grammarly: boolean;
  notranslate: boolean;

  // https://imperavi.com/redactor/docs/settings/paste/
  pastePlainText: boolean;
  pasteLinkTarget: boolean | string;
  pasteImages: boolean;
  pasteLinks: boolean;
  pasteClean: boolean;
  pasteKeepStyle: string[];
  pasteKeepClass: string[];
  pasteKeepAttrs: string[];
  pasteBlockTags: string[];
  pasteInlineTags: string[];

  // https://imperavi.com/redactor/docs/settings/pre/
  preClass: string | boolean;
  preSpaces: number | boolean;

  // https://imperavi.com/redactor/docs/settings/shortcode/
  shortcodes: boolean | object;

  // https://imperavi.com/redactor/docs/settings/shortcut/
  shortcuts: boolean | object;
  shortcutsAdd: boolean | object;

  // https://imperavi.com/redactor/docs/settings/source/
  source: boolean;
  showSource: boolean;

  // https://imperavi.com/redactor/docs/settings/styles/
  styles: boolean;
  stylesClass: string;

  // https://imperavi.com/redactor/docs/settings/tab/
  tabindex: boolean | number;
  tabAsSpaces: boolean | number;
  tabKey: boolean;

  // https://imperavi.com/redactor/docs/settings/toolbar/
  air: boolean;
  toolbar: boolean;
  toolbarFixed: boolean;
  toolbarFixedTarget: Node | string;
  toolbarFixedTopOffset: number;
  toolbarExternal: boolean | string;
  toolbarContext: boolean;

  // https://imperavi.com/redactor/docs/settings/upload/
  dragUpload: boolean;
  multipleUpload: boolean;
  clipboardUpload: boolean;
  uploadData: boolean | object;

  // variable plugin
  variables: string[];
  // custom - quote plugin
  quoteClass: string;

  rtPlaceholders: Placeholder[];
  rtPlaceholdersExcludeParentSelector: string;
  rtLinksExcludeParentSelector: string;
  rtAngularComponentSelectors: string[];
  rtLinkNames: {
    [WysiwygLinkType.Scheduler]: { [key: number]: string };
    [WysiwygLinkType.Questionnaires]: { [key: number]: string };
  };

  // not documented
  inlineTags: string[];
  blockTags: string[];
  context: RedactorConfigContext;
}

export type RedactorConfig = Partial<RedactorSettings>;
